import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  DateTimeInput,
  BooleanInput,
  SelectArrayInput,
  RadioButtonGroupInput,
  required,
  minValue,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  Toolbar,
  SaveButton,
  FormDataConsumer,
  useDataProvider,
  useDelete,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
  Autocomplete,
  TextField as MuiTextField,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
} from '@mui/material';
import {
  LocalOffer,
  Settings,
  People,
  Schedule,
  AttachMoney,
  LocationOn,
  ArrowBack,
  TrendingUp,
  Block,
  Warning,
  Translate as TranslateIcon,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { PromotionMultiLanguageFields } from './PromotionMultiLanguageFields';
import { PromotionTermsManager } from './PromotionTermsManager';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// PromotionsEdit uses snake_case input sources (matching the backend response
// shape). Backend business-rule error paths are emitted as snake_case too, so
// no conversion is needed for field binding. For the summary label we map
// snake_case → camelCase to hit the shared i18n keys.
const PROMOTION_KNOWN_FIELDS_SNAKE = new Set([
    'name', 'description', 'code', 'promotion_type', 'code_type',
    'discount_type', 'discount_value', 'user_type', 'membership_levels',
    'valid_from', 'valid_until', 'time_of_day_start', 'time_of_day_end',
    'days_of_week', 'min_charge_amount', 'max_uses_total', 'max_uses_per_user',
    'priority', 'stackable_with_coupons', 'stackable_with_promotions',
    'is_active', 'sites', 'chargerTypes',
]);

const snakeToCamel = (s: string): string =>
    s.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());

const mapPromotionBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const promotionFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    if (PROMOTION_KNOWN_FIELDS_SNAKE.has(head)) {
        return `resources.promotions.fields.${snakeToCamel(head)}`;
    }
    return field;
};

const validateRequired = required();
const validatePositive = minValue(0);

// Generate time options (00:00 to 23:45 in 15-minute intervals)
const generateTimeOptions = () => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const timeValue = `${hour}:${minute}:00`;
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const period = h < 12 ? 'AM' : 'PM';
      const displayTime = m === 0
        ? `${hour}:${minute} (${displayHour}:${minute} ${period})`
        : `${hour}:${minute}`;
      times.push({ id: timeValue, name: displayTime });
    }
  }
  return times;
};

const DeletePromotionSection = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteOne] = useDelete();
  const translate = useTranslate();

  const handleClick = () => {
    setOpen(true);
    setConfirmText('');
  };

  const handleClose = () => {
    setOpen(false);
    setConfirmText('');
  };

  const handleConfirm = () => {
    if (confirmText === record?.name) {
      void deleteOne(
        'promotions',
        { id: record.id, previousData: record },
        {
          onSuccess: () => {
            notify(translate('resources.promotions.notifications.deleted'));
            redirect('/promotions');
          },
          onError: () => {
            notify(translate('resources.promotions.notifications.error.delete'), { type: 'error' });
          },
        }
      );
      setOpen(false);
    }
  };

  const hasRedemptions = (record?.current_uses || 0) > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {hasRedemptions
            ? translate('resources.promotions.edit.cannotDeleteWithRedemptions')
            : translate('resources.promotions.edit.deleteWarning')}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={hasRedemptions}
        sx={{
          borderColor: hasRedemptions ? '#BDBDBD' : '#F44336',
          color: hasRedemptions ? '#BDBDBD' : '#F44336',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
          '&:hover': {
            borderColor: hasRedemptions ? '#BDBDBD' : '#D32F2F',
            backgroundColor: hasRedemptions ? 'transparent' : 'rgba(244,67,54,0.08)',
          },
          '&.Mui-disabled': {
            borderColor: '#BDBDBD',
            color: '#BDBDBD',
          },
        }}
        title={hasRedemptions ? translate('resources.promotions.edit.deleteButtonTitle') : translate('resources.promotions.edit.deleteButton')}
      >
        {translate('resources.promotions.edit.deleteButton')}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#F44336' }} />
            <Typography variant="h6">{translate('resources.promotions.edit.confirmDeletion')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{translate('resources.promotions.edit.deletionWarningStrong')}</strong>
            </Typography>
            <Typography variant="body2">
              {translate('resources.promotions.edit.deletionWarningText', { name: record?.name })}
            </Typography>
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {translate('resources.promotions.edit.typeToConfirm', { name: record?.name })}
          </Typography>

          <MuiTextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={record?.name}
            variant="outlined"
            autoFocus
            error={confirmText !== '' && confirmText !== record?.name}
            helperText={
              confirmText !== '' && confirmText !== record?.name
                ? translate('resources.promotions.edit.nameDoesNotMatch')
                : ''
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            {translate('resources.promotions.edit.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={confirmText !== record?.name}
            sx={{
              backgroundColor: '#F44336',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#D32F2F',
              },
              '&.Mui-disabled': {
                backgroundColor: '#FFCDD2',
              },
            }}
          >
            {translate('resources.promotions.edit.deletePromotion')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const EditToolbar = () => {
  return (
    <Toolbar
      sx={{
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
        mt: 4,
        '& .MuiButton-root': {
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
        },
      }}
    >
      <SaveButton
        variant="contained"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const now = new Date();
  const validFrom = new Date(record.valid_from);
  const validUntil = new Date(record.valid_until);

  let status = 'inactive';
  let color: 'default' | 'success' | 'error' | 'warning' = 'default';

  if (record.is_active) {
    if (now < validFrom) {
      status = 'scheduled';
      color = 'warning';
    } else if (now >= validFrom && now <= validUntil) {
      status = 'active';
      color = 'success';
    } else {
      status = 'expired';
      color = 'error';
    }
  }

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 4,
    }}>
      <IconButton
        onClick={() => redirect('/promotions')}
        size="large"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#F5F5F5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        }}
        title={translate('resources.promotions.create.backToList')}
      >
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {translate('resources.promotions.edit.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {record.name}
          </Typography>
          <Chip
            label={translate(`resources.promotions.status.${status}`)}
            color={color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// Site selector component
const SiteSelector = () => {
  const { setValue, watch } = useFormContext();
  const dataProvider = useDataProvider();
  const [sites, setSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedSiteIds = watch('sites') || [];

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList('sites', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        setSites(data);
      })
      .catch((error) => {
        console.error('Error fetching sites:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataProvider]);

  // Extract site IDs from objects (backend returns as array of objects with siteId)
  const selectedIds = selectedSiteIds.map((s: any) =>
    typeof s === 'string' ? s : s.siteId || s.site_id
  );
  const selectedSites = sites.filter((site) => selectedIds.includes(site.id));

  const translate = useTranslate();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {translate('resources.promotions.fields.sites')}
      </Typography>
      <Autocomplete
        multiple
        options={sites}
        value={selectedSites}
        onChange={(event, value) => {
          setValue('sites', value.map(site => ({
            siteId: site.id,
            isExcluded: false,
          })));
        }}
        getOptionLabel={(option) => option.name || ''}
        loading={loading}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={selectedSites.length === 0 ? translate('resources.promotions.edit.selectSites') : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[option.code, option.addressLine1 || option.address, option.city].filter(Boolean).join(' • ')}
              </Typography>
            </Box>
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.name}
              size="small"
              {...getTagProps({ index })}
            />
          ))
        }
        sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
      />
    </Box>
  );
};

// Charger type selector component
const ChargerTypeSelector = () => {
  const { setValue, watch } = useFormContext();
  const dataProvider = useDataProvider();
  const [chargerTypes, setChargerTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedTypes = watch('chargerTypes') || [];

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList('charger-types', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        setChargerTypes(data);
      })
      .catch((error) => {
        console.error('Error fetching charger types:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataProvider]);

  const selectedChargerTypes = chargerTypes.filter((ct) => selectedTypes.includes(ct.id));
  const translate = useTranslate();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {translate('resources.promotions.fields.chargerTypes')}
      </Typography>
      <Autocomplete
        multiple
        options={chargerTypes}
        value={selectedChargerTypes}
        onChange={(event, value) => {
          setValue('chargerTypes', value.map(ct => ct.id));
        }}
        getOptionLabel={(option) => option.name || option.id || ''}
        loading={loading}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={selectedChargerTypes.length === 0 ? translate('resources.promotions.edit.selectChargerTypes') : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            <Typography variant="body1">{option.name || option.id}</Typography>
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.name || option.id}
              size="small"
              {...getTagProps({ index })}
            />
          ))
        }
        sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
      />
    </Box>
  );
};

export const PromotionsEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

  const transform = (data: any) => {
    const transformedData = { ...data };

    // Convert datetime to ISO 8601 with timezone (UTC)
    if (data.valid_from) {
      const baseDate = data.valid_from.split('.')[0].split('+')[0].split('Z')[0];
      transformedData.valid_from = baseDate.includes(':00:00') ? `${baseDate}Z` : `${baseDate}:00Z`;
    }
    if (data.valid_until) {
      const baseDate = data.valid_until.split('.')[0].split('+')[0].split('Z')[0];
      transformedData.valid_until = baseDate.includes(':00:00') ? `${baseDate}Z` : `${baseDate}:00Z`;
    }

    // Remove empty optional fields. Strict null/undefined/empty checks so a
    // genuine numeric `0` (e.g. min_charge_amount = 0 meaning "no minimum")
    // reaches the backend instead of being silently coerced to "missing".
    const isMissing = (v: any) =>
      v === undefined || v === null || v === '' ||
      (Array.isArray(v) && v.length === 0);

    if (isMissing(data.chargerTypes)) {
      delete transformedData.chargerTypes;
    }
    if (isMissing(data.timeOfDayStart)) {
      delete transformedData.timeOfDayStart;
    }
    if (isMissing(data.timeOfDayEnd)) {
      delete transformedData.timeOfDayEnd;
    }
    if (isMissing(data.daysOfWeek)) {
      delete transformedData.daysOfWeek;
    }
    if (isMissing(data.membershipLevels)) {
      delete transformedData.membershipLevels;
    }
    if (isMissing(data.minChargeAmount)) {
      delete transformedData.minChargeAmount;
    }
    if (isMissing(data.maxUsesTotal)) {
      delete transformedData.maxUsesTotal;
    }
    if (isMissing(data.maxUsesPerUser)) {
      delete transformedData.maxUsesPerUser;
    }

    return transformedData;
  };

  return (
    <Edit
      actions={false}
      title={false}
      component="div"
      mutationMode="pessimistic"
      transform={transform}
      mutationOptions={{
        onSuccess: () => {
          setServerErrors({});
          notify(translate('resources.promotions.notifications.updated'), { type: 'success' });
          redirect('/promotions');
        },
        onError: (error: any) => {
          const apiError = error?.body || error;
          const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
          const fieldErrors: Record<string, string> = {};
          const summaryLines: string[] = [];
          if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
              const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
              const translated = translateServerMessage(rawMsg, translate);
              const field = mapPromotionBackendPath(path);
              fieldErrors[field] = translated;
              const label = translate(promotionFieldLabelKey(field), { _: field });
              summaryLines.push(`• ${label}: ${translated}`);
            }
          }
          setServerErrors(fieldErrors);

          const headerKey = apiError?.errorCode
            ? `errors.${apiError.errorCode}`
            : 'validation.formHasErrors';
          const header = translate(headerKey, { _: apiError?.message || 'Error' });

          if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, {
              type: 'error',
              multiLine: true,
              autoHideDuration: 10000,
            });
          } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
          }
        },
      }}
      sx={{
        backgroundColor: 'transparent',
        '& .RaEdit-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleForm
        toolbar={<EditToolbar />}
        sx={{
          '& .MuiPaper-root': { backgroundColor: 'transparent', boxShadow: 'none' },
          '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
            px: { xs: 0, sm: 2 },
            py: { xs: 1, sm: 2 },
          },
          padding: { xs: 0, sm: 'inherit' },
        }}
      >
        <ServerErrorBinder errors={serverErrors} />
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 8, sm: 0 } }}>
          <PageHeader />

          {/* Basic Information */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalOffer sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.basicInfo')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="name"
                  label={translate('resources.promotions.fields.name')}
                  validate={validateRequired}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                />

                <TextInput
                  source="description"
                  label={translate('resources.promotions.fields.description')}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Box>
            </CardContent>
          </Card>

          {/* Translations */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <FormDataConsumer>
                {({ formData }) => (
                  <PromotionMultiLanguageFields
                    formData={formData}
                    fields={[
                      {
                        name: 'name',
                        label: translate('resources.promotions.fields.name'),
                        required: true,
                      },
                      {
                        name: 'description',
                        label: translate('resources.promotions.fields.description'),
                        multiline: true,
                        rows: 3,
                      },
                    ]}
                  />
                )}
              </FormDataConsumer>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <FormDataConsumer>
                {({ formData }) => (
                  <PromotionTermsManager formData={formData} />
                )}
              </FormDataConsumer>
            </CardContent>
          </Card>

          {/* Type & Code (Read-only) */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.typeAndCode')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    {translate('resources.promotions.edit.typeCodeLocked')}
                  </Typography>
                </Alert>

                <RadioButtonGroupInput
                  source="promotion_type"
                  label={translate('resources.promotions.fields.promotionType')}
                  choices={[
                    { id: 'coupon', name: translate('resources.promotions.types.coupon') },
                    { id: 'time_limited', name: translate('resources.promotions.types.time_limited') },
                  ]}
                  disabled
                  row
                />

                <FormDataConsumer>
                  {({ formData }) => {
                    if (formData.promotion_type !== 'coupon') return null;

                    // Default to 'shared' if codeType is not set yet
                    const codeType = formData.code_type || 'shared';

                    return (
                      <>
                        <RadioButtonGroupInput
                          source="code_type"
                          label={translate('resources.promotions.fields.codeType')}
                          choices={[
                            { id: 'shared', name: translate('resources.promotions.codeTypes.shared') },
                            { id: 'unique', name: translate('resources.promotions.codeTypes.unique') },
                          ]}
                          helperText={translate('resources.promotions.helpers.codeType')}
                          disabled
                          row
                        />

                        {codeType === 'shared' && (
                          <TextInput
                            source="code"
                            label={translate('resources.promotions.fields.code')}
                            helperText={translate('resources.promotions.helpers.code')}
                            fullWidth
                            validate={validateRequired}
                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                          />
                        )}

                        {codeType === 'unique' && (
                          <Alert severity="info">
                            <Typography variant="body2">
                              {translate('resources.promotions.edit.uniqueCodesManaged')}
                            </Typography>
                          </Alert>
                        )}
                      </>
                    );
                  }}
                </FormDataConsumer>
              </Box>
            </CardContent>
          </Card>

          {/* Discount Configuration */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.discount')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <RadioButtonGroupInput
                  source="discount_type"
                  label={translate('resources.promotions.fields.discountType')}
                  choices={[
                    { id: 'percentage', name: translate('resources.promotions.discountTypes.percentage') },
                    { id: 'fixed_amount', name: translate('resources.promotions.discountTypes.fixed_amount') },
                    { id: 'free_credit', name: translate('resources.promotions.discountTypes.free_credit') },
                  ]}
                  validate={validateRequired}
                  row
                />

                <FormDataConsumer>
                  {({ formData }) => {
                    const discountType = formData.discount_type || 'percentage';
                    let label, helperText, placeholder;

                    if (discountType === 'percentage') {
                      label = translate('resources.promotions.discountFields.percentageLabel');
                      helperText = translate('resources.promotions.discountFields.percentageHelper');
                      placeholder = '50';
                    } else if (discountType === 'fixed_amount') {
                      label = translate('resources.promotions.discountFields.fixedAmountLabel');
                      helperText = translate('resources.promotions.discountFields.fixedAmountHelper');
                      placeholder = '100';
                    } else if (discountType === 'free_credit') {
                      label = translate('resources.promotions.discountFields.freeCreditLabel');
                      helperText = translate('resources.promotions.discountFields.freeCreditHelper');
                      placeholder = '50';
                    }

                    return (
                      <WheelSafeNumberInput
                        source="discount_value"
                        label={label}
                        helperText={helperText}
                        placeholder={placeholder}
                        validate={[validateRequired, validatePositive]}
                        fullWidth
                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                      />
                    );
                  }}
                </FormDataConsumer>

                <WheelSafeNumberInput
                  source="min_charge_amount"
                  label={translate('resources.promotions.fields.minChargeAmount')}
                  helperText={translate('resources.promotions.helpers.minChargeAmount')}
                  validate={validatePositive}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Target Users */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <People sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.targeting')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <RadioButtonGroupInput
                  source="user_type"
                  label={translate('resources.promotions.fields.userType')}
                  choices={[
                    { id: 'app', name: translate('resources.promotions.userTypes.app') },
                    { id: 'rfid', name: translate('resources.promotions.userTypes.rfid') },
                    { id: 'all', name: translate('resources.promotions.userTypes.all') },
                  ]}
                  validate={validateRequired}
                  row
                />

                <SelectArrayInput
                  source="membership_levels"
                  label={translate('resources.promotions.fields.membershipLevels')}
                  choices={[
                    { id: 'standard', name: translate('resources.promotions.memberships.standard') },
                    { id: 'silver', name: translate('resources.promotions.memberships.silver') },
                    { id: 'gold', name: translate('resources.promotions.memberships.gold') },
                    { id: 'platinum', name: translate('resources.promotions.memberships.platinum') },
                    { id: 'vip', name: translate('resources.promotions.memberships.vip') },
                  ]}
                  helperText={translate('resources.promotions.helpers.membershipLevels')}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Validity Period */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Schedule sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.validity')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <DateTimeInput
                      source="valid_from"
                      label={translate('resources.promotions.fields.validFrom')}
                      validate={validateRequired}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <DateTimeInput
                      source="valid_until"
                      label={translate('resources.promotions.fields.validUntil')}
                      validate={validateRequired}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600, color: 'text.secondary' }}>
                  {translate('resources.promotions.sections.timeConstraints')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <SelectInput
                      source="time_of_day_start"
                      label={translate('resources.promotions.fields.timeOfDayStart')}
                      choices={generateTimeOptions()}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <SelectInput
                      source="time_of_day_end"
                      label={translate('resources.promotions.fields.timeOfDayEnd')}
                      choices={generateTimeOptions()}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                </Box>

                <SelectArrayInput
                  source="days_of_week"
                  label={translate('resources.promotions.fields.daysOfWeek')}
                  choices={[
                    { id: 1, name: translate('resources.promotions.days.monday') },
                    { id: 2, name: translate('resources.promotions.days.tuesday') },
                    { id: 3, name: translate('resources.promotions.days.wednesday') },
                    { id: 4, name: translate('resources.promotions.days.thursday') },
                    { id: 5, name: translate('resources.promotions.days.friday') },
                    { id: 6, name: translate('resources.promotions.days.saturday') },
                    { id: 7, name: translate('resources.promotions.days.sunday') },
                  ]}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Usage Limits */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUp sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.limits')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormDataConsumer>
                  {({ formData }) => {
                    const currentUses = formData.current_uses || 0;
                    const maxTotal = formData.max_uses_total;
                    if (currentUses > 0) {
                      return (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          {translate('resources.promotions.edit.usageInfo', { count: currentUses })}
                        </Alert>
                      );
                    }
                    return null;
                  }}
                </FormDataConsumer>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <WheelSafeNumberInput
                      source="max_uses_total"
                      label={translate('resources.promotions.fields.maxUsesTotal')}
                      helperText={translate('resources.promotions.helpers.maxUsesTotal')}
                      validate={validatePositive}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <WheelSafeNumberInput
                      source="max_uses_per_user"
                      label={translate('resources.promotions.fields.maxUsesPerUser')}
                      helperText={translate('resources.promotions.helpers.maxUsesPerUser')}
                      validate={validatePositive}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Restrictions */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.restrictions')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <SiteSelector />
                <ChargerTypeSelector />
              </Box>
            </CardContent>
          </Card>

          {/* Advanced Settings */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.advanced')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <SelectInput
                  source="priority"
                  label={translate('resources.promotions.fields.priority')}
                  helperText={translate('resources.promotions.helpers.priority')}
                  choices={[
                    { id: 1, name: translate('resources.promotions.priorityLevels.1') },
                    { id: 2, name: translate('resources.promotions.priorityLevels.2') },
                    { id: 3, name: translate('resources.promotions.priorityLevels.3') },
                    { id: 4, name: translate('resources.promotions.priorityLevels.4') },
                    { id: 5, name: translate('resources.promotions.priorityLevels.5') },
                    { id: 6, name: translate('resources.promotions.priorityLevels.6') },
                    { id: 7, name: translate('resources.promotions.priorityLevels.7') },
                    { id: 8, name: translate('resources.promotions.priorityLevels.8') },
                    { id: 9, name: translate('resources.promotions.priorityLevels.9') },
                    { id: 10, name: translate('resources.promotions.priorityLevels.10') },
                  ]}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <BooleanInput
                    source="stackable_with_coupons"
                    label={translate('resources.promotions.fields.stackableWithCoupons')}
                  />

                  <BooleanInput
                    source="stackable_with_promotions"
                    label={translate('resources.promotions.fields.stackableWithPromotions')}
                  />

                  <BooleanInput
                    source="is_active"
                    label={translate('resources.promotions.fields.isActive')}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card sx={{ mb: 3, borderColor: '#F44336', borderWidth: 1, borderStyle: 'solid', overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Block sx={{ mr: 2, color: '#F44336' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#F44336' }}>
                  {translate('resources.promotions.edit.dangerZone')}
                </Typography>
              </Box>

              <DeletePromotionSection />
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Edit>
  );
};
